import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useUrlState = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const setUrlState = useCallback(
    (newState = {}, replace = false) => {
      const searchParams = new URLSearchParams(location.search);

      if (Object.keys(newState).length === 0 && newState.constructor === Object) {
        const allKeys = Array.from(searchParams.keys());
        allKeys.forEach((key) => searchParams.delete(key));
      } else {
        Object.entries(newState).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            searchParams.delete(key);
          } else {
            searchParams.set(key, value.toString());
          }
        });
      }

      const newSearch = searchParams.toString();
      const fullPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      if (fullPath !== `${location.pathname}${location.search}` || replace) {
        navigate(fullPath, { replace });
      }
    },
    [location.search, navigate, location.pathname]
  );

  const clearUrlState = useCallback(
    (keys: string[] = []) => {
      const searchParams = new URLSearchParams(location.search);

      if (keys.length === 0) {
        // Clear all search parameters
        Array.from(searchParams.keys()).forEach((key) => searchParams.delete(key));
      } else {
        // Clear specific keys
        keys.forEach((key) => searchParams.delete(key));
      }

      const newSearch = searchParams.toString();
      const fullPath = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      if (fullPath !== `${location.pathname}${location.search}`) {
        navigate(fullPath, { replace: true });
      }
    },
    [navigate, location.pathname, location.search]
  );

  const getUrlState = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [location.search]);

  return { setUrlState, clearUrlState, getUrlState };
};
